import type { GaVueComponent } from "@/common/vueUtils";
import type { ContentMetadata } from "@newgenerated/shared/schema";
import type { TranslationFn } from "@utils/vue-migration/translationUtils";
import type { DeepReadonly } from "vue";
import { GaBadge } from "@/components/general/GaBadge";

export function ContentTypeBadge(props: { content: DeepReadonly<ContentMetadata>; t: TranslationFn }): GaVueComponent {
  const { t } = props;

  const getSourceIcon = (): string | undefined => {
    if (props.content.contentType !== "SUMMARY") return undefined;
    const icons = {
      BOOK: "ico-book-open",
      ARTICLE: "ico-article",
      PODCAST: "ico-mic",
      REPORT: "ico-file-pdf",
      VIDEO: "ico-video",
    };
    return icons[props.content.sourceType as keyof typeof icons];
  };

  switch (props.content.contentType) {
    case "SUMMARY": {
      const icon = getSourceIcon();

      if (props.content.documentType === "REVIEW") {
        return (
          <GaBadge icon={icon} variant="neutral-warm-light" caption={true}>
            {t("summary:source.REVIEW")}
          </GaBadge>
        );
      } else if (props.content.previewAvailable) {
        return (
          <GaBadge icon="ico-clock" variant="muted" caption={true}>
            {t("summary:source.UPCOMING")}
          </GaBadge>
        );
      } else {
        return (
          <GaBadge icon={icon} variant="primary-light" caption={true}>
            {t(`summary:source.${props.content.sourceType}`)}
          </GaBadge>
        );
      }
    }

    case "ACTIONABLE":
      return (
        <GaBadge icon="ico-graduation" caption={true} variant="purple-light">
          {t("actionable:source.actionable")}
        </GaBadge>
      );

    case "ONLINE_CONTENT":
      return (
        <>
          <GaBadge caption={true} variant="success-light" icon={"ico-globe"}>
            {t(`verify:type.${props.content.formatType}`)}
          </GaBadge>
          <GaBadge variant={"success-light"}>
            <i class="ico-star-fill" aria-label={props.t("verify:detailView.contentRating")} />
            {props.content.overallRating}
          </GaBadge>
        </>
      );

    case "MIAB":
      return (
        <>
          <GaBadge icon="ico-people" caption={true} variant="orange-light">
            {t("miab:source.miab")}
          </GaBadge>
          {props.content.customerId !== null ? <GaBadge variant="orange-light" icon="ico-note" /> : null}
        </>
      );

    case "EXPERT_VIDEO":
      return (
        <GaBadge icon="ico-video" caption={true} variant="secondary-light">
          {t("expertVideos:source.expertVideos")}
        </GaBadge>
      );

    case "QUICKWIN": {
      const quickWinType = props.content.quickWinType === "INSIGHT" ? t("quickwin:insight") : t("quickwin:reflection");
      return (
        <GaBadge icon="ico-zap" caption={true} variant="warning-light">
          {quickWinType}
        </GaBadge>
      );
    }
  }
}
